import { useState, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown, ChevronUp, Loader2, Pencil, RefreshCw, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { InvoiceHeader } from '@/components/invoice/InvoiceHeader';
import { InvoiceDetailsTab } from '@/components/invoice/InvoiceDetailsTab';
import AdvancedComplianceTab from '@/components/invoice/AdvancedComplianceTab';
import AIComplianceTab from '@/components/invoice/AIComplianceTab';
import { InvoiceComplianceCards } from '@/components/invoice/InvoiceComplianceCards';
import { InvoiceItemsComparisonTable } from '@/components/invoice/InvoiceItemsComparisonTable';
import { AIComplianceSummaryBar } from '@/components/invoice/AIComplianceSummaryBar';
import { InvoiceSupportingDocs } from '@/components/invoice/InvoiceSupportingDocs';
import RecordMetadata from '@/components/RecordMetadata';
import ExternalSystemInfo from '@/components/ExternalSystemInfo';
import { EntityStatusActions } from '@/components/status/EntityStatusActions';
import { ResponsiveSplit } from '@/components/layout/ResponsiveSplit';
import { useAuth } from '@/contexts/AuthContext';
import { useAccessControl } from '@/contexts/AccessControlContext';
import { Modules, Permissions } from '@prolarus/shared';
import { supabase } from '@/integrations/supabase/client';
import {
  invoiceService,
  invoiceItemService,
  ticketService,
  ticketItemService,
  priceBookService,
  priceBookItemService,
  profileService,
  externalSystemService,
  companyService,
  complianceCheckService,
  complianceEngineService,
  statusService,
} from '@/services';
import type { ComplianceLineItem } from '@/services';
import { VendorNotificationPopout, type NonComplianceMessage } from '@/components/invoice/VendorNotificationPopout';

export type NonComplianceFilter = 'qtyMismatch' | 'priceMismatch' | 'unsupportedItem' | 'noTicketSupport' | null;

export default function InvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { canPerformAction } = useAccessControl();
  const canEdit = canPerformAction(Modules.COMPLIANCE, Permissions.invoice.update.code);
  const [activeTab, setActiveTab] = useState('detail');
  const [complianceFilter, setComplianceFilter] = useState<NonComplianceFilter>(null);
  const [selectedPriceBookItemIds, setSelectedPriceBookItemIds] = useState<string[]>([]);
  const [supportingDocsTab, setSupportingDocsTab] = useState<string>('ticket');
  const [notificationMessages, setNotificationMessages] = useState<NonComplianceMessage[] | null>(null);
  const [headerOpen, setHeaderOpen] = useState(true);

  const handleComplianceCardClick = (filter: NonComplianceFilter) => {
    setComplianceFilter(filter);
    setActiveTab('line-items');
  };

  const handleLineItemSelectionChange = useCallback((priceBookItemIds: string[]) => {
    setSelectedPriceBookItemIds(priceBookItemIds);
    if (priceBookItemIds.length > 0) {
      setSupportingDocsTab('pricebook');
    }
  }, []);

  const handleMainTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
    if (tab !== 'line-items') {
      setSelectedPriceBookItemIds([]);
      setSupportingDocsTab('ticket');
    }
  }, []);

  // Fetch invoice with vendor details
  const { data: invoice, isLoading: isLoadingInvoice } = useQuery({
    queryKey: ['invoice', id],
    queryFn: async () => {
      const result = await invoiceService.getWithVendor(id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!id,
  });

  // Fetch invoice items
  const { data: invoiceItems = [] } = useQuery({
    queryKey: ['invoice-items', id],
    queryFn: async () => {
      const result = await invoiceItemService.listByInvoice(id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!id,
  });

  // Fetch associated ticket (via invoice_id on tickets table)
  const { data: ticket } = useQuery({
    queryKey: ['invoice-ticket', id],
    queryFn: async () => {
      const result = await ticketService.getByInvoice(id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!id,
  });

  // Fetch ticket items
  const { data: ticketItems = [] } = useQuery({
    queryKey: ['ticket-items', ticket?.id],
    queryFn: async () => {
      const result = await ticketItemService.listByTicket(ticket!.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!ticket?.id,
  });

  // Fetch price book - only if invoice has an assigned price book
  const { data: priceBook } = useQuery({
    queryKey: ['invoice-pricebook', invoice?.price_book_id],
    queryFn: async () => {
      const result = await priceBookService.get(invoice!.price_book_id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!invoice?.price_book_id,
  });

  // Fetch price book items
  const { data: priceBookItems = [] } = useQuery({
    queryKey: ['pricebook-items', priceBook?.id],
    queryFn: async () => {
      const result = await priceBookItemService.listActiveByPriceBook(priceBook!.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!priceBook?.id,
  });

  // Get profile for company_id
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const result = await profileService.get(user!.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  // Fetch status readonly flag
  const { data: invoiceStatus } = useQuery({
    queryKey: ['invoice-status', invoice?.status_id],
    queryFn: async () => {
      const result = await statusService.get(invoice!.status_id!);
      if (result.error) return null;
      return result.data;
    },
    enabled: !!invoice?.status_id,
  });
  const isReadonly = invoiceStatus?.readonly ?? false;

  // Fetch company for email
  const { data: company } = useQuery({
    queryKey: ['company', profile?.company_id],
    queryFn: async () => {
      const result = await companyService.get(profile!.company_id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!profile?.company_id,
  });

  // Lookup external system ID for linking
  const { data: externalSystemDetails } = useQuery({
    queryKey: ['external-system-by-name', invoice?.external_system, profile?.company_id],
    queryFn: async () => {
      if (!invoice?.external_system || !profile?.company_id) return null;
      const result = await externalSystemService.getByName(invoice.external_system, profile.company_id);
      if (result.error) return null;
      return result.data;
    },
    enabled: !!invoice?.external_system && !!profile?.company_id,
  });

  // Fetch list of external systems for edit dropdown
  const { data: externalSystems = [] } = useQuery({
    queryKey: ['external-systems', profile?.company_id],
    queryFn: async () => {
      const result = await externalSystemService.list(profile!.company_id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!profile?.company_id,
  });

  // ---- Compliance Checks (persisted) ----
  const { data: complianceChecks = [], isLoading: isLoadingChecks } = useQuery({
    queryKey: ['compliance-checks', id],
    queryFn: async () => {
      const result = await complianceCheckService.listByInvoice(id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!id,
  });

  // Fetch latest AI compliance engine run (for inline detail on Line Items tab)
  const { data: complianceEngineRun } = useQuery({
    queryKey: ['compliance-engine-run', id],
    queryFn: () => complianceEngineService.latestByInvoice(id!),
    enabled: !!id,
  });

  const complianceLineItems: ComplianceLineItem[] = complianceEngineRun?.results?.line_items ?? [];

  const runComplianceCheck = useMutation({
    mutationFn: async () => {
      if (!id || !profile?.company_id) throw new Error('Missing context');
      const result = await complianceCheckService.runForInvoice(id, {
        companyId: profile.company_id,
        userId: user?.id,
      });
      if (result.error) throw result.error;
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance-checks', id] });
      toast({ title: 'Compliance check complete', description: 'Results have been saved.' });
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  // Determine stale state: if invoice items were modified after last check
  const lastCheckTime = complianceChecks.length > 0 ? complianceChecks[0].created : null;

  // Handler for saving external system changes
  const handleSaveExternalSystem = async (system: string | null, recordId: string | null) => {
    if (!invoice || !profile?.company_id || !user) return;

    const snapshot = {
      external_system: invoice.external_system,
      external_system_record_id: invoice.external_system_record_id,
    };

    const result = await invoiceService.update(
      id!,
      { external_system: system, external_system_record_id: recordId },
      snapshot,
      { companyId: profile.company_id, userId: user.id }
    );

    if (result.error) {
      toast({ title: 'Error', description: 'Failed to update external system link.', variant: 'destructive' });
      throw result.error;
    }

    queryClient.invalidateQueries({ queryKey: ['invoice', id] });
    queryClient.invalidateQueries({ queryKey: ['external-system-by-name', system, profile.company_id] });
    toast({ title: 'Updated', description: 'External system link has been updated.' });
  };

  // Handle status change with history tracking via service
  const handleStatusChange = async (newStatusId: string, _statusName: string, comments?: string) => {
    if (!id || !profile?.company_id || !user?.id) return;

    const result = await invoiceService.updateStatus(
      id,
      newStatusId,
      invoice?.status_id || null,
      { companyId: profile.company_id, userId: user.id },
      { comments }
    );

    if (result.error) {
      toast({ title: 'Error', description: 'Failed to update status.', variant: 'destructive' });
    } else {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      toast({ title: 'Status updated', description: 'Invoice status has been updated.' });
    }
  };

  const handleDeleteTicket = async () => {
    if (!ticket || !profile?.company_id || !user?.id) return;
    try {
      const result = await ticketService.delete(
        ticket.id,
        ticket as unknown as Record<string, unknown>,
        { companyId: profile.company_id, userId: user.id }
      );
      if (result.error) throw result.error;
      queryClient.invalidateQueries({ queryKey: ['invoice-ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket-items', ticket.id] });
      setSupportingDocsTab('invoice');
      toast({ title: 'Deleted', description: 'Field ticket and associated files have been deleted.' });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete field ticket.';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    }
  };

  const complianceIssueCount = useMemo(() => complianceChecks.filter(
    c => c.qty_mismatch_on_ticket_item || c.price_mismatch_on_price_book_item ||
         c.price_mismatch_on_ticket_item || c.missing_price_book_item || c.missing_ticket_item
  ).length, [complianceChecks]);

  if (isLoadingInvoice) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">Invoice not found.</p>
        <Button variant="outline" onClick={() => navigate('/invoices')}>
          Back to Invoices
        </Button>
      </div>
    );
  }

  const hasChecks = complianceChecks.length > 0;

  const formatCollapsedTotal = (amount: number) => {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className="animate-slide-up">
      {/* Back button + Edit */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/invoices')}
          className="-ml-2"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Invoices
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => runComplianceCheck.mutate()}
            disabled={runComplianceCheck.isPending || !profile?.company_id}
          >
            {runComplianceCheck.isPending ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : hasChecks ? (
              <RefreshCw className="w-4 h-4 mr-1" />
            ) : (
              <ShieldCheck className="w-4 h-4 mr-1" />
            )}
            {hasChecks ? 'Re-run Surface Checks' : 'Run Surface Checks'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/invoices/${id}/edit`)}
            disabled={isReadonly || !canEdit}
          >
            <Pencil className="w-4 h-4 mr-1" />
            Edit
          </Button>
        </div>
      </div>

      {/* Collapsible Invoice Header + Compliance Cards */}
      <Collapsible open={headerOpen} onOpenChange={setHeaderOpen}>
        {/* Collapsed summary bar */}
        {!headerOpen && (
          <CollapsibleTrigger asChild>
            <div className="bg-card border border-border rounded-lg px-5 py-3 mb-6 flex items-center gap-6 cursor-pointer">
              <div className="text-left shrink-0">
                <span className="text-base font-bold text-foreground">
                  Invoice #{invoice.invoice_number || '—'}
                </span>
                <span className="block text-sm text-muted-foreground">
                  {invoice.vendor?.name || 'No vendor'}
                </span>
              </div>
              <div className="text-left whitespace-nowrap">
                <span className="block text-xs text-muted-foreground">Total</span>
                <span className="text-sm font-semibold text-foreground">
                  {formatCollapsedTotal(invoice.total_amount || 0)} {invoice.currency || 'USD'}
                </span>
              </div>
              <div className="text-left whitespace-nowrap">
                <span className="block text-xs text-muted-foreground">Invoice Date</span>
                <span className="text-sm font-semibold text-foreground">
                  {invoice.invoice_date ? format(new Date(invoice.invoice_date), 'MMM d, yyyy') : '—'}
                </span>
              </div>
              <div className="text-left whitespace-nowrap">
                <span className="block text-xs text-muted-foreground">Service Date</span>
                <span className="text-sm font-semibold text-foreground">
                  {invoice.service_date ? format(new Date(invoice.service_date), 'MMM d, yyyy') : '—'}
                </span>
              </div>
              <div className="text-left whitespace-nowrap">
                <span className="block text-xs text-muted-foreground">Due Date</span>
                <span className="text-sm font-semibold text-foreground">
                  {invoice.due_date ? format(new Date(invoice.due_date), 'MMM d, yyyy') : '—'}
                </span>
              </div>
              {hasChecks && (
                <div className={`text-sm whitespace-nowrap ${complianceIssueCount > 0 ? 'text-destructive font-medium' : 'text-green-600 dark:text-green-400'}`}>
                  {complianceIssueCount > 0
                    ? `${complianceIssueCount} surface level issue${complianceIssueCount !== 1 ? 's' : ''}`
                    : 'No surface level issues'}
                </div>
              )}
              {invoiceStatus?.name && (
                <div className="ml-auto text-sm font-medium text-muted-foreground whitespace-nowrap">
                  {invoiceStatus.name}
                </div>
              )}
              <ChevronDown className={`${invoiceStatus?.name ? '' : 'ml-auto '}h-5 w-5 text-muted-foreground`} />
            </div>
          </CollapsibleTrigger>
        )}

        <CollapsibleContent>
          {/* Invoice Header (75%) + Status Workflow (25%) + Collapse Chevron */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-3">
                <InvoiceHeader
                  invoice={{
                    invoice_number: invoice.invoice_number,
                    vendor_id: invoice.vendor_id,
                    vendor_name: invoice.vendor?.name,
                    total_amount: invoice.total_amount || 0,
                    currency: invoice.currency,
                    invoice_date: invoice.invoice_date,
                    service_date: invoice.service_date,
                    due_date: invoice.due_date,
                  }}
                  vendorAddress={invoice.vendor ? {
                    address: invoice.vendor.address,
                    city: invoice.vendor.city,
                    state: invoice.vendor.state,
                    country: invoice.vendor.country,
                  } : null}
                  priceBook={priceBook ? {
                    id: priceBook.id,
                    name: priceBook.name,
                  } : null}
                />
              </div>
              <div className="bg-card border border-border rounded-lg p-5 h-full">
                <EntityStatusActions
                  entityType="invoice"
                  currentStatusId={invoice.status_id}
                  onStatusChange={handleStatusChange}
                />
              </div>
            </div>
            <CollapsibleTrigger asChild>
              <button className="flex items-center self-start pt-2 text-muted-foreground hover:text-foreground transition-colors">
                <ChevronUp className="h-5 w-5" />
              </button>
            </CollapsibleTrigger>
          </div>

          {/* Compliance Cards - full width */}
          <div className="mb-6">
            {hasChecks ? (
              <InvoiceComplianceCards
                complianceChecks={complianceChecks}
                onCardClick={handleComplianceCardClick}
                lastCheckTime={lastCheckTime}
                engineLineItems={complianceLineItems}
              />
            ) : (
              <InvoiceComplianceCards
                complianceChecks={[]}
                onCardClick={handleComplianceCardClick}
                lastCheckTime={null}
                engineLineItems={complianceLineItems}
              />
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleMainTabChange} className="w-full">
        <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0 mb-6">
          <TabsTrigger value="detail" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3">Detail</TabsTrigger>
          <TabsTrigger value="line-items" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3">Line Items</TabsTrigger>
          <TabsTrigger value="advanced-compliance" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3">MSA Validation</TabsTrigger>
          <TabsTrigger value="ai-compliance" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3">Review Compliance</TabsTrigger>
        </TabsList>

        <TabsContent value="detail">
          <ResponsiveSplit
            mobile={
              <div className="flex flex-col gap-6">
            <div>
              <InvoiceDetailsTab
                invoice={{
                  invoice_number: invoice.invoice_number,
                  vendor_id: invoice.vendor_id,
                  vendor_name: invoice.vendor?.name,
                  po_number: invoice.po_number,
                  invoice_date: invoice.invoice_date,
                  service_date: invoice.service_date,
                  payment_terms: invoice.payment_terms,
                  due_date: invoice.due_date,
                  discount: invoice.discount,
                  discount_amt: invoice.discount_amt,
                  discount_expiration: invoice.discount_expiration,
                  subtotal: invoice.subtotal,
                  tax_amount: invoice.tax_amount,
                  total_amount: invoice.total_amount,
                  currency: invoice.currency,
                  notes: invoice.notes,
                }}
                vendorAddress={invoice.vendor ? {
                  address: invoice.vendor.address,
                  city: invoice.vendor.city,
                  state: invoice.vendor.state,
                  country: invoice.vendor.country,
                } : null}
                priceBook={priceBook ? {
                  id: priceBook.id,
                  name: priceBook.name,
                } : null}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <RecordMetadata record={invoice} />
                <ExternalSystemInfo
                  externalSystem={invoice.external_system}
                  externalSystemRecordId={invoice.external_system_record_id}
                  externalSystemId={externalSystemDetails?.id}
                  availableSystems={externalSystems.map(s => ({ id: s.id, name: s.name }))}
                  onSave={handleSaveExternalSystem}
                />
              </div>
            </div>
            <InvoiceSupportingDocs
              invoiceFilePath={invoice.source_file_path || null}
              ticketFilePath={ticket?.source_file_path || null}
              priceBookId={priceBook?.id ?? null}
              usedPriceBookItemIds={invoiceItems
                .map(item => item.price_book_item_id)
                .filter((id): id is string => id != null)}
              extractionInfo={{
                extraction_method: invoice.extraction_method,
                extraction_confidence: invoice.extraction_confidence,
                ai_provider: invoice.ai_provider,
                source_filename: invoice.source_filename,
              }}
              ticketExtractionInfo={ticket ? {
                extraction_method: ticket.extraction_method,
                extraction_confidence: ticket.extraction_confidence,
                ai_provider: ticket.ai_provider,
                source_filename: ticket.source_filename,
              } : null}
              ticketId={ticket?.id}
              onDeleteTicket={handleDeleteTicket}
            />
              </div>
            }
            desktop={
              <div className="flex">
            <ResizablePanelGroup direction="horizontal">
              <ResizablePanel defaultSize={65} minSize={40}>
                <div className="pr-4">
                  <InvoiceDetailsTab
                    invoice={{
                      invoice_number: invoice.invoice_number,
                      vendor_id: invoice.vendor_id,
                      vendor_name: invoice.vendor?.name,
                      po_number: invoice.po_number,
                      invoice_date: invoice.invoice_date,
                      service_date: invoice.service_date,
                      payment_terms: invoice.payment_terms,
                      due_date: invoice.due_date,
                      discount: invoice.discount,
                      discount_amt: invoice.discount_amt,
                      discount_expiration: invoice.discount_expiration,
                      subtotal: invoice.subtotal,
                      tax_amount: invoice.tax_amount,
                      total_amount: invoice.total_amount,
                      currency: invoice.currency,
                      notes: invoice.notes,
                    }}
                    vendorAddress={invoice.vendor ? {
                      address: invoice.vendor.address,
                      city: invoice.vendor.city,
                      state: invoice.vendor.state,
                      country: invoice.vendor.country,
                    } : null}
                    priceBook={priceBook ? {
                      id: priceBook.id,
                      name: priceBook.name,
                    } : null}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <RecordMetadata record={invoice} />
                    <ExternalSystemInfo
                      externalSystem={invoice.external_system}
                      externalSystemRecordId={invoice.external_system_record_id}
                      externalSystemId={externalSystemDetails?.id}
                      availableSystems={externalSystems.map(s => ({ id: s.id, name: s.name }))}
                      onSave={handleSaveExternalSystem}
                    />
                  </div>
                </div>
              </ResizablePanel>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize={35} minSize={20}>
                <div className="pl-4">
                  <InvoiceSupportingDocs
                    invoiceFilePath={invoice.source_file_path || null}
                    ticketFilePath={ticket?.source_file_path || null}
                    priceBookId={priceBook?.id ?? null}
                    usedPriceBookItemIds={invoiceItems
                      .map(item => item.price_book_item_id)
                      .filter((id): id is string => id != null)}
                    extractionInfo={{
                      extraction_method: invoice.extraction_method,
                      extraction_confidence: invoice.extraction_confidence,
                      ai_provider: invoice.ai_provider,
                      source_filename: invoice.source_filename,
                    }}
                    ticketExtractionInfo={ticket ? {
                      extraction_method: ticket.extraction_method,
                      extraction_confidence: ticket.extraction_confidence,
                      ai_provider: ticket.ai_provider,
                      source_filename: ticket.source_filename,
                    } : null}
                    ticketId={ticket?.id}
                    onDeleteTicket={handleDeleteTicket}
                  />
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
              </div>
            }
          />
        </TabsContent>

        <TabsContent value="line-items">
          <ResponsiveSplit
            mobile={
              <div className="flex flex-col gap-6">
            {complianceEngineRun && (
              <div className="mb-4">
                <AIComplianceSummaryBar
                  metadata={complianceEngineRun.metadata}
                  lineItems={complianceLineItems}
                />
              </div>
            )}
            <InvoiceItemsComparisonTable
              invoiceItems={invoiceItems.map((item) => ({
                id: item.id,
                sequence: item.sequence,
                description: item.description,
                item_code: item.item_code,
                quantity: item.quantity ? Number(item.quantity) : null,
                uom: item.uom,
                unit_price: item.unit_price ? Number(item.unit_price) : null,
                line_total: item.line_total ? Number(item.line_total) : null,
                price_book_item_id: item.price_book_item_id,
                discount: item.discount ? Number(item.discount) : null,
              }))}
              ticketItems={ticketItems.map((item) => ({
                id: item.id,
                description: item.description,
                item_code: item.item_code,
                quantity: item.quantity ? Number(item.quantity) : null,
                uom: item.uom,
                unit_price: item.unit_price ? Number(item.unit_price) : null,
                discount: item.discount ? Number(item.discount) : null,
                line_total: item.line_total ? Number(item.line_total) : null,
                price_book_item_id: item.price_book_item_id,
              }))}
              priceBookItems={priceBookItems.map((item) => ({
                id: item.id,
                description: item.description,
                itemcode: item.itemcode,
                price: Number(item.price),
                discount: Number(item.discount),
                unit: item.unit,
                uom_id: item.uom_id,
              }))}
              hasPriceBook={!!invoice.price_book_id || !!priceBook}
              hasTicket={!!ticket}
              complianceFilter={complianceFilter}
              onComplianceFilterClear={() => setComplianceFilter(null)}
              onSelectionChange={handleLineItemSelectionChange}
              onNotificationRequest={setNotificationMessages}
              complianceChecks={complianceChecks}
              complianceLineItems={complianceLineItems}
            />
            <InvoiceSupportingDocs
              invoiceFilePath={invoice.source_file_path || null}
              ticketFilePath={ticket?.source_file_path || null}
              priceBookId={priceBook?.id ?? null}
              usedPriceBookItemIds={invoiceItems
                .map(item => item.price_book_item_id)
                .filter((id): id is string => id != null)}
              activeTab={supportingDocsTab}
              onTabChange={setSupportingDocsTab}
              focusedPriceBookItemIds={selectedPriceBookItemIds}
              ticketExtractionInfo={ticket ? {
                extraction_method: ticket.extraction_method,
                extraction_confidence: ticket.extraction_confidence,
                ai_provider: ticket.ai_provider,
                source_filename: ticket.source_filename,
              } : null}
              ticketId={ticket?.id}
              onDeleteTicket={handleDeleteTicket}
            />
              </div>
            }
            desktop={
              <div className="flex">
            <ResizablePanelGroup direction="horizontal">
              <ResizablePanel defaultSize={65} minSize={40}>
                <div className="pr-4">
                  {complianceEngineRun && (
                    <div className="mb-4">
                      <AIComplianceSummaryBar
                        metadata={complianceEngineRun.metadata}
                        lineItems={complianceLineItems}
                      />
                    </div>
                  )}
                  <InvoiceItemsComparisonTable
                    invoiceItems={invoiceItems.map((item) => ({
                      id: item.id,
                      sequence: item.sequence,
                      description: item.description,
                      item_code: item.item_code,
                      quantity: item.quantity ? Number(item.quantity) : null,
                      uom: item.uom,
                      unit_price: item.unit_price ? Number(item.unit_price) : null,
                      line_total: item.line_total ? Number(item.line_total) : null,
                      price_book_item_id: item.price_book_item_id,
                      discount: item.discount ? Number(item.discount) : null,
                    }))}
                    ticketItems={ticketItems.map((item) => ({
                      id: item.id,
                      description: item.description,
                      item_code: item.item_code,
                      quantity: item.quantity ? Number(item.quantity) : null,
                      uom: item.uom,
                      unit_price: item.unit_price ? Number(item.unit_price) : null,
                      discount: item.discount ? Number(item.discount) : null,
                      line_total: item.line_total ? Number(item.line_total) : null,
                      price_book_item_id: item.price_book_item_id,
                    }))}
                    priceBookItems={priceBookItems.map((item) => ({
                      id: item.id,
                      description: item.description,
                      itemcode: item.itemcode,
                      price: Number(item.price),
                      discount: Number(item.discount),
                      unit: item.unit,
                      uom_id: item.uom_id,
                    }))}
                    hasPriceBook={!!invoice.price_book_id || !!priceBook}
                    hasTicket={!!ticket}
                    complianceFilter={complianceFilter}
                    onComplianceFilterClear={() => setComplianceFilter(null)}
                    onSelectionChange={handleLineItemSelectionChange}
                    onNotificationRequest={setNotificationMessages}
                    complianceChecks={complianceChecks}
                    complianceLineItems={complianceLineItems}
                  />
                </div>
              </ResizablePanel>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize={35} minSize={20}>
                <div className="pl-4">
                  <InvoiceSupportingDocs
                    invoiceFilePath={invoice.source_file_path || null}
                    ticketFilePath={ticket?.source_file_path || null}
                    priceBookId={priceBook?.id ?? null}
                    usedPriceBookItemIds={invoiceItems
                      .map(item => item.price_book_item_id)
                      .filter((id): id is string => id != null)}
                    activeTab={supportingDocsTab}
                    onTabChange={setSupportingDocsTab}
                    focusedPriceBookItemIds={selectedPriceBookItemIds}
                    ticketExtractionInfo={ticket ? {
                      extraction_method: ticket.extraction_method,
                      extraction_confidence: ticket.extraction_confidence,
                      ai_provider: ticket.ai_provider,
                      source_filename: ticket.source_filename,
                    } : null}
                    ticketId={ticket?.id}
                    onDeleteTicket={handleDeleteTicket}
                  />
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
              </div>
            }
          />
        </TabsContent>

        <TabsContent value="advanced-compliance">
          <AdvancedComplianceTab
            invoiceId={id!}
            priceBookId={invoice.price_book_id || null}
          />
        </TabsContent>

        <TabsContent value="ai-compliance">
          <AIComplianceTab
            invoiceId={id!}
            priceBookId={invoice.price_book_id || null}
          />
        </TabsContent>
      </Tabs>

      {notificationMessages && (
        <VendorNotificationPopout
          open={true}
          onClose={() => setNotificationMessages(null)}
          vendorName={invoice.vendor?.name || 'Unknown Vendor'}
          vendorPhone={invoice.vendor?.phone || null}
          vendorEmail={invoice.vendor?.email || null}
          invoiceNumber={invoice.invoice_number}
          invoiceDate={invoice.invoice_date}
          invoiceTotal={invoice.total_amount || 0}
          currency={invoice.currency}
          userEmail={user?.email || ''}
          companyEmail={company?.email || null}
          nonComplianceMessages={notificationMessages}
        />
      )}
    </div>
  );
}
